import dayjs from 'dayjs';

import { AssignmentType } from '../types';
import { AssignmentActivePeriodResponse } from '../types/dto/response/planner-assignment-response';
import { mapAssignmentRateFromDTO } from './assignment-rate.mapper';

type MappedActivePeriod = AssignmentType['activePeriods'][number];

interface MapActivePeriodsResult {
  periods: MappedActivePeriod[];
  hasRateChanges: boolean;
}

/**
 * Maps a single active period from DTO to domain model.
 */
const mapActivePeriodFromDTO = (period: AssignmentActivePeriodResponse): MappedActivePeriod => ({
  startDate: dayjs(period.dateRange.startDate),
  endDate: dayjs(period.dateRange.endDate),
  rate: period.rate ? mapAssignmentRateFromDTO(period.rate) : null,
  rateChangeDate: null,
});

/**
 * Maps active periods from API response to domain model.
 * Fills null rates by carrying forward from previous periods.
 * Tracks rate change dates so the UI knows when each rate became effective.
 * If no valid activePeriods provided, creates a single period from the assignment's full date range.
 */
export const mapActivePeriodsFromDTO = (
  activePeriods: AssignmentActivePeriodResponse[] | undefined,
  fallbackStartDate: string,
  fallbackEndDate: string
): MapActivePeriodsResult => {
  if (!activePeriods || activePeriods.length === 0) {
    return {
      periods: [
        {
          startDate: dayjs(fallbackStartDate),
          endDate: dayjs(fallbackEndDate),
          rate: null,
          rateChangeDate: null,
        },
      ],
      hasRateChanges: false,
    };
  }

  const mapped = activePeriods
    .filter(period => period.dateRange.endDate !== null)
    .map(mapActivePeriodFromDTO);

  // Carry forward rates and track rate change dates
  let lastRate: MappedActivePeriod['rate'] = null;
  let lastRateChangeDate: dayjs.Dayjs | null = null;
  const distinctRates = new Set<number>();

  for (const period of mapped) {
    if (period.rate !== null) {
      // API returned a rate — this period introduced a new rate
      lastRate = period.rate;
      lastRateChangeDate = period.startDate;
      period.rateChangeDate = period.startDate;
      distinctRates.add(period.rate.rate);
    } else if (lastRate !== null) {
      // API returned null — carry forward the previous rate
      period.rate = lastRate;
      period.rateChangeDate = lastRateChangeDate;
    }
    // If lastRate is still null (very first period had no rate), leave rate and rateChangeDate as null
  }

  return {
    periods: mapped,
    hasRateChanges: distinctRates.size > 1,
  };
};
